import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Card from '../../components/common/Card/Card';
import Modal from '../../components/common/Modal/Modal';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ButtonSpinner from '../../components/common/ButtonSpinner/ButtonSpinner';
import CompetitionStatus, { cwMeta } from '../../components/common/CompetitionStatus/CompetitionStatus';
import ReviewPanel from '../../components/admin/CompetitionWorkflow/ReviewPanel';
import { useToast } from '../../context/ToastContext';
import useAuth from '../../hooks/useAuth';
import { updateTemplate } from '../../services/competitionSetupService';
import {
  listCompetitionTemplates, getCompetitionTemplate, getCohort, assignCohort,
} from '../../services/competitionWorkflowService';
import { withdrawStudent, reinstateStudent } from '../../services/competitionSetupService';
import { getAllStudents } from '../../services/studentService';
import { formatDate } from '../../utils/helpers';
import styles from './CompetitionWorkflow.module.css';

// The grid columns come from the selected template's own component tree, so
// every competition gets its own columns rather than one hard-coded set.
//
// A group is rolled up into one column, because it is one thing to hand in. A
// group that is a multi-step WORKFLOW — script -> slides -> recording, where
// each step unlocks the next — is expanded so each step gets its own dot to
// track, since the whole point is watching the chain advance.
//
// What makes it a workflow is the unlocking, not the section types. Mixed types
// used to stand in for that, back when the only mixed group in the system was
// IRIS's video chain. It stopped meaning the same thing once a competition
// could be a long form: Diana's nine sections are all mixed (a date, a
// dropdown, some prose) and none of them unlocks anything, so that rule would
// have drawn one grid column per question — about seventy of them.
const buildGridColumns = (requirements) => {
  const cols = [];
  (requirements || []).forEach((r) => {
    const children = r.children || [];
    const isWorkflow = r.submission_type === 'group'
      && children.length > 0
      && children.some((c) => c.unlock_after_key);
    if (isWorkflow) {
      children.forEach((c) => cols.push({ key: c.req_key, label: c.title }));
    } else {
      cols.push({ key: r.req_key, label: r.title });
    }
  });
  return cols;
};

// A withdrawn enrollment is a status, not a delete, so the row keeps its
// submissions, deadlines and roll-ups — everything that counts a student has to
// skip it explicitly.
const isWithdrawn = (row) => row?.status === 'withdrawn' || Boolean(row?.withdrawn_at);

const todayISO = () => new Date().toISOString().slice(0, 10);

// A competition is done once its final date is behind us. No final date means
// "no known close date", which is an open competition, not a finished one.
const isCompleted = (t, today) => !!t.final_deadline && t.final_deadline < today;

// Nearest deadline first. A competition with no date set sinks to the bottom:
// there is nothing to chase on it.
const byApproaching = (a, b) => {
  if (!a.final_deadline) return b.final_deadline ? 1 : 0;
  if (!b.final_deadline) return -1;
  return a.final_deadline.localeCompare(b.final_deadline);
};
// Completed ones always carry a date, so the most recently closed comes first.
const byRecentlyClosed = (a, b) => b.final_deadline.localeCompare(a.final_deadline);

const daysUntil = (dateStr, today) => Math.round(
  (Date.parse(`${dateStr}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000,
);

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const deadlineNote = (t, today) => {
  if (!t.final_deadline) return 'No final date set';
  const days = daysUntil(t.final_deadline, today);
  if (days === 0) return 'Final date is today';
  if (days > 0) return `${plural(days, 'day')} left`;
  return `Closed ${plural(Math.abs(days), 'day')} ago`;
};

const FILTERS = [
  { key: 'all', label: 'All students' },
  { key: 'awaiting_review', label: 'Awaiting review' },
  { key: 'behind', label: 'Behind schedule (overdue)' },
  { key: 'changes', label: 'Needs re-submission' },
  { key: 'ready', label: 'Fully ready' },
];

// Case-insensitive subsequence match: every query char appears in order in text.
// Also true for a plain substring, so "roy", "rsou" and "sounak" all match "Roy Sounak".
const fuzzyMatch = (query, text) => {
  const q = (query || '').toLowerCase().replace(/\s+/g, '');
  if (!q) return true;
  const t = (text || '').toLowerCase();
  let i = 0;
  for (let c = 0; c < t.length && i < q.length; c += 1) {
    if (t[c] === q[i]) i += 1;
  }
  return i === q.length;
};

// One column of the index. Each row is the selector for that competition, so
// the page keeps a single cohort view below rather than a second screen.
const CompetitionIndexList = ({ items, selectedId, onSelect, today, emptyNote, done = false }) => {
  if (items.length === 0) return <p className={styles.indexEmpty}>{emptyNote}</p>;
  return (
    <ul className={styles.indexList}>
      {items.map((t) => {
        const days = t.final_deadline ? daysUntil(t.final_deadline, today) : null;
        const urgent = !done && days !== null && days <= 7;
        const classes = [styles.indexCard];
        if (t.id === selectedId) classes.push(styles.indexCardActive);
        if (done) classes.push(styles.indexCardDone);
        return (
          <li key={t.id}>
            <button
              type="button"
              className={classes.join(' ')}
              onClick={() => onSelect(t.id)}
              aria-current={t.id === selectedId ? 'true' : undefined}
            >
              <span className={styles.indexName}>{t.name}</span>
              <span className={styles.indexMeta}>
                <span className={urgent ? styles.indexUrgent : undefined}>{deadlineNote(t, today)}</span>
                <i className={styles.indexSep} />
                <span>{t.final_deadline ? formatDate(t.final_deadline) : 'no date'}</span>
                <i className={styles.indexSep} />
                <span>{plural(t.enrollment_count || 0, 'student')}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const CompetitionWorkflow = () => {
  const toast = useToast();
  const { user } = useAuth();
  // Mentors get the same review powers (approve / request changes / deadline)
  // on their own students only — building the cohort stays with admins.
  const canAssign = user?.role === 'super_admin' || user?.role === 'manager';
  // Matches COMPETITION_ADMIN_ROLES on the withdraw endpoint. Mentors reach
  // this page too and must not be offered an action that would 403.
  const canManageCohort = ['super_admin', 'manager', 'poc_owner'].includes(user?.role);
  const [removing, setRemoving] = useState(null);   // the row being taken off
  const [removeReason, setRemoveReason] = useState('');
  const [removeBusy, setRemoveBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  // The tab lists 247 templates but only ~10 have anyone enrolled. Empty ones
  // are hidden by default; the toggle is what keeps a brand-new competition
  // reachable, since you have to select it to assign its first student.
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [template, setTemplate] = useState(null);
  const [cohort, setCohort] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [assignOpen, setAssignOpen] = useState(false);
  const [reviewEnrollment, setReviewEnrollment] = useState(null);
  // The reminder cut-off. Held separately from `template` so the input is
  // editable without rewriting the loaded template on every keystroke.
  const [deadline, setDeadline] = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);

  // --- initial load ------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const tmpls = await listCompetitionTemplates();
        setTemplates(tmpls);
        const today = todayISO();
        // Land on the competition that needs attention soonest: an ongoing one
        // with the nearest deadline. If no competition has students, show
        // everything rather than an empty page.
        const mostUrgent = (list) => [...list].sort((a, b) => {
          const aDone = isCompleted(a, today);
          if (aDone !== isCompleted(b, today)) return aDone ? 1 : -1;
          return aDone ? byRecentlyClosed(a, b) : byApproaching(a, b);
        })[0];
        const enrolled = tmpls.filter((t) => (t.enrollment_count || 0) > 0);
        if (enrolled.length) {
          setTemplateId(mostUrgent(enrolled).id);
        } else if (tmpls.length) {
          setShowAllTemplates(true);
          setTemplateId(mostUrgent(tmpls).id);
        } else {
          setLoading(false);
        }
      } catch (e) {
        toast.error(e.message || 'Failed to load competitions.');
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDeadline = async () => {
    setSavingDeadline(true);
    try {
      // Empty clears it, which means "no known close date" — reminders then
      // keep running rather than stopping on a date nobody set.
      const updated = await updateTemplate(templateId, { final_deadline: deadline || null });
      setTemplate(updated);
      setDeadline(updated?.final_deadline || '');
      toast.success(deadline
        ? 'Reminder cut-off updated.'
        : 'Cut-off cleared — reminders will keep running.');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not update the date.');
    } finally {
      setSavingDeadline(false);
    }
  };

  const loadCohort = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [tmpl, rows] = await Promise.all([getCompetitionTemplate(id), getCohort(id)]);
      setTemplate(tmpl);
      setDeadline(tmpl?.final_deadline || '');
      setCohort(rows);
    } catch (e) {
      toast.error(e.message || 'Failed to load cohort.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (templateId) loadCohort(templateId); }, [templateId, loadCohort]);

  // Taking a student off a competition is a status change, not a delete: their
  // drafts, uploads and review comments stay attached, so putting them back is
  // one click rather than a recovery job.
  const handleRemove = async () => {
    setRemoveBusy(true);
    try {
      await withdrawStudent(removing.enrollment_id, removeReason || null);
      toast.success(`${removing.student_name || removing.student_email} removed from this competition.`);
      setRemoving(null);
      loadCohort(templateId);
    } catch (e) {
      toast.error(e.message || 'Could not remove the student.');
    } finally {
      setRemoveBusy(false);
    }
  };

  const handleReinstate = async (row) => {
    try {
      await reinstateStudent(row.enrollment_id);
      toast.success(`${row.student_name || row.student_email} put back on this competition.`);
      loadCohort(templateId);
    } catch (e) {
      toast.error(e.message || 'Could not reinstate the student.');
    }
  };

  const gridColumns = useMemo(
    () => buildGridColumns(template?.requirements),
    [template],
  );

  // --- summary counts ----------------------------------------------------
  const stats = useMemo(() => {
    const today = todayISO();
    // Withdrawn students stay in the grid (their work and the reinstate action
    // live there) but they are not applicants any more, and their leftover
    // submissions and deadlines must not show up as awaiting review or behind.
    const active = cohort.filter((r) => !isWithdrawn(r));
    let ready = 0, awaiting = 0, behind = 0;
    active.forEach((r) => {
      if (r.overall === 'ready') ready += 1;
      if (Object.values(r.statuses || {}).some((s) => s === 'submitted')) awaiting += 1;
      if (r.next_deadline && r.next_deadline < today) behind += 1;
    });
    return { total: active.length, ready, awaiting, behind };
  }, [cohort]);

  // --- filtering ---------------------------------------------------------
  const filteredCohort = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return cohort.filter((r) => {
      if (search && !fuzzyMatch(search, `${r.student_name} ${r.student_email}`)) return false;
      const values = Object.values(r.statuses || {});
      switch (filter) {
        case 'awaiting_review': return values.some((s) => s === 'submitted');
        case 'behind': return r.next_deadline && r.next_deadline < today && r.overall !== 'ready';
        case 'changes': return values.some((s) => s === 'changes_required');
        case 'ready': return r.overall === 'ready';
        default: return true;
      }
    });
  }, [cohort, filter, search]);

  // --- the competition index: ongoing vs completed ------------------------
  const today = todayISO();
  const enrolledTemplates = templates.filter((t) => (t.enrollment_count || 0) > 0);
  const visibleTemplates = showAllTemplates ? templates : enrolledTemplates;
  const hiddenCount = templates.length - enrolledTemplates.length;

  const { ongoing, completed } = useMemo(() => {
    const open = [];
    const done = [];
    visibleTemplates.forEach((t) => (isCompleted(t, today) ? done : open).push(t));
    open.sort(byApproaching);
    done.sort(byRecentlyClosed);
    return { ongoing: open, completed: done };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, showAllTemplates, today]);

  if (loading && !template) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  if (!templates.length) {
    return (
      <div className={styles.page}>
        <h1>Competitions</h1>
        <Card><p className={styles.empty}>No competition templates found. Seed a competition to get started.</p></Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.masthead}>
        <div className={styles.mastheadTop}>
          <span className={styles.eyebrow}>Competition Showcase</span>
          <div className={styles.headerActions}>
            {hiddenCount > 0 && (
              <label className={styles.showAll} title="Competitions with no students enrolled">
                <input
                  type="checkbox"
                  checked={showAllTemplates}
                  onChange={(e) => setShowAllTemplates(e.target.checked)}
                />
                Show all ({hiddenCount} empty)
              </label>
            )}
            {canAssign && (
              <button className={styles.primaryBtn} onClick={() => setAssignOpen(true)}>Assign Students</button>
            )}
          </div>
        </div>
      </header>

      {/* The index: every competition split by whether its final date has passed,
          ongoing ones ordered by the deadline coming up soonest. */}
      <section className={styles.index}>
        <div className={styles.indexGroup}>
          <div className={styles.indexHead}>
            <span className={styles.indexTitle}>Ongoing</span>
            <span className={styles.indexCount}>{ongoing.length}</span>
            <span className={styles.indexHint}>deadline soonest first</span>
          </div>
          <CompetitionIndexList
            items={ongoing}
            selectedId={templateId}
            onSelect={setTemplateId}
            today={today}
            emptyNote="Nothing open right now."
          />
        </div>
        <div className={styles.indexGroup}>
          <div className={styles.indexHead}>
            <span className={styles.indexTitle}>Completed</span>
            <span className={styles.indexCount}>{completed.length}</span>
            <span className={styles.indexHint}>most recently closed first</span>
          </div>
          <CompetitionIndexList
            items={completed}
            selectedId={templateId}
            onSelect={setTemplateId}
            today={today}
            emptyNote="No competition has closed yet."
            done
          />
        </div>
      </section>

      <header className={styles.detailHead}>
        <h1 className={styles.title}>{template ? template.name : 'Competitions'}</h1>
        {template && (
          <span className={`${styles.pill} ${isCompleted(template, today) ? styles.pillDone : styles.pillOpen}`}>
            {isCompleted(template, today) ? 'Completed' : 'Ongoing'}
            <i className={styles.pillSep} />
            {deadlineNote(template, today)}
          </span>
        )}
        {template && (canManageCohort ? (
          <div className={styles.deadlineEditor}>
            <label htmlFor="finalDeadline">Final date — reminders stop after this</label>
            <input
              id="finalDeadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              disabled={savingDeadline}
            />
            <button
              className={styles.smallBtn}
              onClick={saveDeadline}
              disabled={savingDeadline || (deadline || '') === (template.final_deadline || '')}
            >
              {savingDeadline ? 'Saving…' : 'Save'}
            </button>
            {template.final_deadline ? (
              <span className={styles.deadlineHint}>
                currently {formatDate(template.final_deadline)}
                {template.final_deadline < today && ' — passed, reminders stopped'}
              </span>
            ) : (
              <span className={styles.deadlineHint}>not set — reminders keep running</span>
            )}
          </div>
        ) : (
          template.final_deadline && (
            <p className={styles.meta}>Final submission — <strong>{formatDate(template.final_deadline)}</strong></p>
          )
        ))}
      </header>

      {/* By the numbers */}
      <div className={styles.statStrip}>
        <div className={styles.stat}>
          <span className={styles.statNum}>{stats.total}</span>
          <span className={styles.statLabel}>Applicants</span>
        </div>
        <span className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statNum}>{stats.ready}</span>
          <span className={styles.statLabel}><i className={styles.dot} style={{ background: '#43A047' }} />Fully ready</span>
        </div>
        <span className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statNum}>{stats.awaiting}</span>
          <span className={styles.statLabel}><i className={styles.dot} style={{ background: '#1E88E5' }} />Awaiting review</span>
        </div>
        <span className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statNum}>{stats.behind}</span>
          <span className={styles.statLabel}><i className={styles.dot} style={{ background: '#E53935' }} />Behind schedule</span>
        </div>
      </div>

      {/* Search + filters */}
      <div className={styles.controls}>
        <div className={styles.filters}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`${styles.filterBtn} ${filter === f.key ? styles.filterActive : ''}`}
              onClick={() => setFilter(f.key)}
            >{f.label}</button>
          ))}
        </div>
        <div className={styles.searchWrap}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search students…" />
        </div>
      </div>

      {/* Control panel grid */}
      <section className={styles.tableSection}>
        <div className={styles.sectionLabel}>The Cohort</div>
        {cohort.length === 0 ? (
          <p className={styles.empty}>
            {canAssign
              ? 'No students assigned yet. Use “Assign Students” to build the cohort.'
              : 'None of your students are enrolled in this competition yet.'}
          </p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.grid}>
              <thead>
                <tr>
                  <th className={styles.stickyCol}>Student</th>
                  {gridColumns.map((c) => <th key={c.key}>{c.label}</th>)}
                  <th>Progress</th>
                  <th>Overall</th>
                  <th>Next Deadline</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredCohort.length === 0 && (
                  <tr>
                    <td colSpan={gridColumns.length + 5} className={styles.noMatch}>
                      No students match your search or filter.
                    </td>
                  </tr>
                )}
                {filteredCohort.map((r) => {
                  const today = new Date().toISOString().slice(0, 10);
                  const overdue = r.next_deadline && r.next_deadline < today && r.overall !== 'ready';
                  const withdrawn = isWithdrawn(r);
                  return (
                    <tr key={r.enrollment_id} className={withdrawn ? styles.withdrawnRow : ''}>
                      <td className={styles.stickyCol}>
                        <div className={styles.studentName}>{r.student_name || r.student_email}</div>
                        {withdrawn && <span className={styles.withdrawnTag}>Withdrawn</span>}
                      </td>
                      {gridColumns.map((c) => (
                        <td key={c.key} className={styles.dotCell}>
                          <CompetitionStatus status={r.statuses?.[c.key] || 'not_started'} compact
                            title={`${c.label}: ${cwMeta(r.statuses?.[c.key]).label}`} />
                        </td>
                      ))}
                      <td>{r.progress_done}/{r.progress_total}</td>
                      <td><CompetitionStatus status={r.overall === 'ready' ? 'approved' : 'in_progress'} /></td>
                      <td className={overdue ? styles.overdue : ''}>
                        {r.next_deadline ? formatDate(r.next_deadline) : '—'}
                      </td>
                      <td className={styles.rowActions}>
                        <button className={styles.reviewBtn} onClick={() => setReviewEnrollment(r)}>Review →</button>
                        {canManageCohort && (
                          withdrawn ? (
                            <button
                              className={styles.reinstateBtn}
                              onClick={() => handleReinstate(r)}
                            >
                              Reinstate
                            </button>
                          ) : (
                            <button
                              className={styles.removeBtn}
                              onClick={() => { setRemoving(r); setRemoveReason(''); }}
                            >
                              Remove
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Assign modal */}
      {assignOpen && canAssign && (
        <AssignModal
          templateId={templateId}
          enrolledIds={new Set(cohort.map((r) => r.student_id))}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => { setAssignOpen(false); loadCohort(templateId); }}
        />
      )}

      {/* Remove-from-competition confirmation */}
      <Modal
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${removing.student_name || removing.student_email}?` : ''}
      >
        {removing && (
          <div className={styles.removeBody}>
            <p>
              They come off this competition and it disappears from their workspace.
              Their drafts, uploads and review comments are kept, and you can put
              them back at any time.
            </p>
            <label className={styles.removeLabel}>
              Reason (optional)
              <input
                className={styles.removeInput}
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder="Why are they coming off?"
              />
            </label>
            <div className={styles.removeActions}>
              <button className={styles.cancelBtn} onClick={() => setRemoving(null)} disabled={removeBusy}>
                Cancel
              </button>
              <button className={styles.confirmRemoveBtn} onClick={handleRemove} disabled={removeBusy}>
                {removeBusy && <ButtonSpinner size={12} />}Remove from competition
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Review modal */}
      <Modal
        isOpen={!!reviewEnrollment}
        onClose={() => setReviewEnrollment(null)}
        title={reviewEnrollment ? `Review — ${reviewEnrollment.student_name || reviewEnrollment.student_email}` : ''}
        wide
      >
        {reviewEnrollment && (
          <ReviewPanel
            enrollmentId={reviewEnrollment.enrollment_id}
            onChanged={() => loadCohort(templateId)}
          />
        )}
      </Modal>
    </div>
  );
};

// --- Assign students modal -------------------------------------------------
const AssignModal = ({ templateId, enrolledIds, onClose, onAssigned }) => {
  const toast = useToast();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllStudents();
        setStudents(data);
      } catch (e) {
        toast.error(e.message || 'Failed to load students.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const available = students.filter((s) => {
    if (enrolledIds.has(s.id)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return `${s.first_name} ${s.last_name} ${s.email}`.toLowerCase().includes(q);
  });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const assign = async () => {
    if (selected.size === 0) { toast.warning('Select at least one student.'); return; }
    setSaving(true);
    try {
      const res = await assignCohort(templateId, Array.from(selected));
      toast.success(`Assigned ${res.assigned} student(s)${res.skipped ? `, ${res.skipped} already enrolled` : ''}.`);
      onAssigned();
    } catch (e) {
      toast.error(e.message || 'Assignment failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Assign Students to Competition" wide>
      {loading ? <LoadingSpinner /> : (
        <div>
          <input
            className={styles.searchInput}
            placeholder="Search students…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.studentList}>
            {available.length === 0 ? (
              <p className={styles.empty}>No students available to add.</p>
            ) : available.map((s) => (
              <label key={s.id} className={styles.studentRow}>
                <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                <span>{s.first_name} {s.last_name}</span>
                <span className={styles.studentEmail}>{s.email}</span>
              </label>
            ))}
          </div>
          <div className={styles.modalFooter}>
            <span>{selected.size} selected</span>
            <button className={styles.primaryBtn} disabled={saving} onClick={assign}>
              {saving && <ButtonSpinner size={13} />}{saving ? 'Assigning…' : `Assign ${selected.size || ''}`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CompetitionWorkflow;
